import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Collapse,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Badge,
    CircularProgress,
} from '@mui/material';

const NAV = '#131F2A';

const textFieldSx = (extraSx = {}) => ({
    '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        '&:hover fieldset': { borderColor: NAV },
        '&.Mui-focused fieldset': { borderColor: NAV, borderWidth: '1.5px' },
    },
    ...extraSx,
});

const labelSx = { '&.Mui-focused': { color: NAV } };

const selectInputSx = {
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: NAV },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: NAV, borderWidth: '1.5px' },
};

const CourseFilterBar = ({
    filters,
    onChange,
    onSearch,
    onClear,
    courseTypes = [],
    categories = [],
    subcategories = [],
    mentors = [],
    loadingDropdowns,
    loadingResults,
    activePanelFilterCount,
}) => {
    const [panelOpen, setPanelOpen] = useState(false);

    const filteredSubcategories = filters.subcategory__category_id
        ? subcategories.filter(s => String(s.category?.id) === String(filters.subcategory__category_id))
        : subcategories;

    const handleCategoryChange = (value) => {
        onChange('subcategory__category_id', value);
        onChange('subcategory_id', '');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') onSearch();
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder="Buscar por título..."
                    value={filters.title__icontains}
                    onChange={e => onChange('title__icontains', e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={textFieldSx({ flex: 2 })}
                />
                <TextField
                    size="small"
                    placeholder="Código..."
                    value={filters.code}
                    onChange={e => onChange('code', e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={textFieldSx({ flex: 1 })}
                />
                <Badge
                    badgeContent={activePanelFilterCount || 0}
                    sx={{ '& .MuiBadge-badge': { backgroundColor: NAV, color: '#fff' } }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setPanelOpen(prev => !prev)}
                        sx={{
                            whiteSpace: 'nowrap',
                            borderColor: NAV,
                            color: NAV,
                            '&:hover': { borderColor: NAV, backgroundColor: 'rgba(19,31,42,0.04)' },
                        }}
                    >
                        Filtros
                    </Button>
                </Badge>
                <Button
                    variant="contained"
                    size="small"
                    onClick={onSearch}
                    disabled={loadingResults}
                    sx={{
                        whiteSpace: 'nowrap',
                        backgroundColor: NAV,
                        boxShadow: 'none',
                        minWidth: 90,
                        '&:hover': { backgroundColor: '#1e2f3e', boxShadow: 'none' },
                        '&.Mui-disabled': { backgroundColor: 'rgba(19,31,42,0.5)', color: '#fff' },
                    }}
                >
                    {loadingResults
                        ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                        : 'Buscar'
                    }
                </Button>
            </Box>

            <Collapse in={panelOpen}>
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" disabled={loadingDropdowns}>
                                <InputLabel sx={labelSx}>Tipo de curso</InputLabel>
                                <Select sx={selectInputSx} label="Tipo de curso"
                                    value={filters.course_type_id}
                                    onChange={e => onChange('course_type_id', e.target.value)}>
                                    <MenuItem value="">Todos</MenuItem>
                                    {loadingDropdowns
                                        ? <MenuItem disabled>Cargando...</MenuItem>
                                        : courseTypes.map(ct => <MenuItem key={ct.id} value={ct.id}>{ct.name}</MenuItem>)
                                    }
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={labelSx}>Estado</InputLabel>
                                <Select sx={selectInputSx} label="Estado"
                                    value={filters.is_active}
                                    onChange={e => onChange('is_active', e.target.value)}>
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="true">Activo</MenuItem>
                                    <MenuItem value="false">Inactivo</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={labelSx}>Gratuito</InputLabel>
                                <Select sx={selectInputSx} label="Gratuito"
                                    value={filters.is_free}
                                    onChange={e => onChange('is_free', e.target.value)}>
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="true">Sí</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={labelSx}>Destacado</InputLabel>
                                <Select sx={selectInputSx} label="Destacado"
                                    value={filters.is_featured}
                                    onChange={e => onChange('is_featured', e.target.value)}>
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="true">Sí</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={labelSx}>Gratis usuario público</InputLabel>
                                <Select sx={selectInputSx} label="Gratis usuario público"
                                    value={filters.is_free_public_user}
                                    onChange={e => onChange('is_free_public_user', e.target.value)}>
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="true">Sí</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={labelSx}>Tipo de evaluación</InputLabel>
                                <Select sx={selectInputSx} label="Tipo de evaluación"
                                    value={filters.assessment_type}
                                    onChange={e => onChange('assessment_type', e.target.value)}>
                                    <MenuItem value="">Todos</MenuItem>
                                    <MenuItem value="CATEGORY">Por categoría</MenuItem>
                                    <MenuItem value="COURSE">Por curso</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" disabled={loadingDropdowns}>
                                <InputLabel sx={labelSx}>Categoría</InputLabel>
                                <Select sx={selectInputSx} label="Categoría"
                                    value={filters.subcategory__category_id}
                                    onChange={e => handleCategoryChange(e.target.value)}>
                                    <MenuItem value="">Todas</MenuItem>
                                    {loadingDropdowns
                                        ? <MenuItem disabled>Cargando...</MenuItem>
                                        : categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)
                                    }
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" disabled={loadingDropdowns}>
                                <InputLabel sx={labelSx}>Subcategoría</InputLabel>
                                <Select sx={selectInputSx} label="Subcategoría"
                                    value={filters.subcategory_id}
                                    onChange={e => onChange('subcategory_id', e.target.value)}>
                                    <MenuItem value="">Todas</MenuItem>
                                    {loadingDropdowns
                                        ? <MenuItem disabled>Cargando...</MenuItem>
                                        : filteredSubcategories.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)
                                    }
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" disabled={loadingDropdowns}>
                                <InputLabel sx={labelSx}>Mentor</InputLabel>
                                <Select sx={selectInputSx} label="Mentor"
                                    value={filters.mentor_id}
                                    onChange={e => onChange('mentor_id', e.target.value)}>
                                    <MenuItem value="">Todos</MenuItem>
                                    {loadingDropdowns
                                        ? <MenuItem disabled>Cargando...</MenuItem>
                                        : mentors.map(m => (
                                            <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                        </Grid>

                    </Grid>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={onClear}
                            sx={{
                                borderColor: 'rgba(19,31,42,0.3)',
                                color: NAV,
                                '&:hover': { borderColor: NAV, backgroundColor: 'rgba(19,31,42,0.04)' },
                            }}
                        >
                            Limpiar filtros
                        </Button>
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default CourseFilterBar;
